import { useMemo, useState } from 'react';
import { useMutation, useQuery } from 'urql';
import { useNavigate } from 'react-router-dom';
import PageHeader from '@/components/common/PageHeader.jsx';
import Field from '@/components/common/Field.jsx';
import EmptyState from '@/components/common/EmptyState.jsx';
import { useToast, useKeyboardShortcuts } from '@/hooks/useKeyboardShortcuts.js';
import { ASISTENCIAS_LIST, ASISTENCIA_DELETE } from '@/graphql/rrhh/asistencias.gql.js';
import exportCsv from '@/utils/exportCsv.js';

const ESTADOS = [
  { label: 'Todos', value: '' },
  { label: 'En curso', value: 'EnCurso' },
  { label: 'Completa', value: 'Completa' },
  { label: 'Ausente', value: 'Ausente' },
  { label: 'Tarde', value: 'Tarde' },
];

const ORDENES = [
  { label: 'Fecha descendente', value: 'fecha:DESC' },
  { label: 'Fecha ascendente', value: 'fecha:ASC' },
  { label: 'Empleado', value: 'empleadoNombre:ASC' },
];

const INITIAL_FILTERS = {
  empleado: '',
  sucursal: '',
  estado: '',
  fechaDesde: '',
  fechaHasta: '',
};

const INITIAL_PAGINATION = {
  page: 1,
  perPage: 10,
};

function mapFilters(filters) {
  return {
    empleadoId: filters.empleado ? Number(filters.empleado) : null,
    sucursalId: filters.sucursal ? Number(filters.sucursal) : null,
    estado: filters.estado || null,
    fechaDesde: filters.fechaDesde || null,
    fechaHasta: filters.fechaHasta || null,
  };
}

function formatDate(value) {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return new Intl.DateTimeFormat('es-AR', { year: 'numeric', month: 'short', day: '2-digit' }).format(date);
}

function formatTime(value) {
  if (!value) return '';
  if (value.includes('T')) {
    const date = new Date(value);
    if (!Number.isNaN(date.getTime())) {
      return date.toLocaleTimeString('es-AR', { hour: '2-digit', minute: '2-digit' });
    }
  }
  return value.slice(0, 5);
}

function getEstadoClass(estado) {
  switch (estado) {
    case 'Completa':
      return 'bg-[rgba(var(--brand),0.12)] text-[rgb(var(--brand))]';
    case 'Tarde':
      return 'bg-[rgba(var(--brand-700),0.18)] text-[rgb(var(--brand-700))]';
    case 'Ausente':
      return 'bg-[rgba(var(--fg),0.08)] text-[rgb(var(--fg))]';
    default:
      return 'bg-[rgba(var(--muted),0.18)] text-[rgba(var(--muted),0.95)]';
  }
}

export default function ListadoAsistencias() {
  const navigate = useNavigate();
  const toast = useToast();
  const [filterForm, setFilterForm] = useState(INITIAL_FILTERS);
  const [filters, setFilters] = useState(INITIAL_FILTERS);
  const [pagination, setPagination] = useState(INITIAL_PAGINATION);
  const [order, setOrder] = useState({ field: 'fecha', direction: 'DESC' });

  const [{ data, fetching, error }] = useQuery({
    query: ASISTENCIAS_LIST,
    variables: {
      filters: mapFilters(filters),
      pagination,
      order,
    },
  });

  const [, deleteAsistencia] = useMutation(ASISTENCIA_DELETE);

  const asistencias = data?.asistencias?.items ?? [];
  const pageInfo = data?.asistencias?.pageInfo ?? { ...pagination, totalItems: asistencias.length, totalPages: 1 };
  const summary = data?.asistencias?.summary ?? { totalHoras: 0, llegadasTarde: 0, ausencias: 0 };

  const isEmpty = !fetching && asistencias.length === 0;

  useKeyboardShortcuts();

  const handleSubmitFilters = (event) => {
    event.preventDefault();
    setFilters(filterForm);
    setPagination(prev => ({ ...prev, page: 1 }));
  };

  const handleOrderChange = (event) => {
    const [field, direction] = event.target.value.split(':');
    setOrder({ field, direction });
  };

  const handleExport = () => {
    if (!asistencias.length) {
      toast.info('No hay asistencias para exportar con los filtros actuales.');
      return;
    }
    exportCsv({
      filename: 'asistencias.csv',
      columns: [
        { label: 'ID', key: 'id' },
        { label: 'Empleado', key: 'empleadoNombre' },
        { label: 'Sucursal', key: 'sucursalNombre' },
        { label: 'Fecha', key: 'fecha' },
        { label: 'Check-in', key: 'checkIn' },
        { label: 'Check-out', key: 'checkOut' },
        { label: 'Método', key: 'metodo' },
        { label: 'Estado', key: 'estado' },
        { label: 'Notas', key: 'notas' },
      ],
      rows: asistencias,
    });
    toast.success('Exportamos el CSV con las asistencias del periodo seleccionado.');
  };

  const handleDelete = async (id) => {
    const confirmed = window.confirm('¿Eliminar la asistencia seleccionada?');
    if (!confirmed) return;
    const result = await deleteAsistencia({ id });
    if (result.error) {
      toast.error(result.error?.message || 'No pudimos eliminar la asistencia.');
      return;
    }
    toast.success('Asistencia eliminada correctamente.');
  };

  const formattedSummary = useMemo(() => ({
    totalHoras: Number(summary.totalHoras || 0).toFixed(1),
    llegadasTarde: summary.llegadasTarde || 0,
    ausencias: summary.ausencias || 0,
  }), [summary]);

  return (
    <div className="w-full">
      <PageHeader
        title="Asistencias"
        description="Gestioná fichadas de ingreso y salida con controles de puntualidad."
        breadcrumbs={[
          { label: 'RRHH', href: '/rrhh' },
          { label: 'Asistencias' },
        ]}
        actions={(
          <div className="flex flex-wrap gap-2">
            <button
              type="button"
              onClick={handleExport}
              className="rounded-xl border border-[rgba(var(--border),0.5)] bg-[rgba(var(--panel),0.7)] px-4 py-2 text-sm font-semibold text-[rgb(var(--fg))] shadow-sm transition focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[rgb(var(--ring))]"
            >
              Exportar CSV
            </button>
            <button
              type="button"
              onClick={() => navigate('/rrhh/asistencias/nueva')}
              className="rounded-xl bg-[rgb(var(--brand))] px-4 py-2 text-sm font-semibold text-white shadow-md transition focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[rgb(var(--ring))] hover:bg-[rgb(var(--brand-700))]"
            >
              Registrar asistencia
            </button>
          </div>
        )}
      />

      <section className="mx-auto w-full max-w-[1400px] space-y-6">
        <div className="grid grid-cols-12 gap-4">
          <div className="col-span-12 md:col-span-4">
            <article className="h-full rounded-2xl border border-[rgba(var(--border),0.6)] bg-[rgba(var(--panel),0.6)] p-4 shadow-[0_8px_30px_rgba(var(--shadow),0.12)]">
              <p className="text-sm font-medium text-[rgba(var(--muted),0.9)]">Horas trabajadas</p>
              <p className="mt-2 text-2xl font-semibold text-[rgb(var(--fg))]">{formattedSummary.totalHoras}</p>
            </article>
          </div>
          <div className="col-span-12 md:col-span-4">
            <article className="h-full rounded-2xl border border-[rgba(var(--border),0.6)] bg-[rgba(var(--panel),0.6)] p-4 shadow-[0_8px_30px_rgba(var(--shadow),0.12)]">
              <p className="text-sm font-medium text-[rgba(var(--muted),0.9)]">Llegadas tarde</p>
              <p className="mt-2 text-2xl font-semibold text-[rgb(var(--fg))]">{formattedSummary.llegadasTarde}</p>
            </article>
          </div>
          <div className="col-span-12 md:col-span-4">
            <article className="h-full rounded-2xl border border-[rgba(var(--border),0.6)] bg-[rgba(var(--panel),0.6)] p-4 shadow-[0_8px_30px_rgba(var(--shadow),0.12)]">
              <p className="text-sm font-medium text-[rgba(var(--muted),0.9)]">Ausencias</p>
              <p className="mt-2 text-2xl font-semibold text-[rgb(var(--fg))]">{formattedSummary.ausencias}</p>
            </article>
          </div>
        </div>

        <div className="rounded-2xl border border-[rgba(var(--border),0.6)] bg-[rgba(var(--panel),0.95)] p-6 shadow-[0_8px_30px_rgba(var(--shadow),0.15)]">
          <form onSubmit={handleSubmitFilters} className="grid grid-cols-12 gap-4">
            <div className="col-span-12 md:col-span-3">
              <Field label="Empleado" htmlFor="empleado" hint="ID del colaborador">
                <input
                  id="empleado"
                  value={filterForm.empleado}
                  onChange={event => setFilterForm(prev => ({ ...prev, empleado: event.target.value }))}
                  placeholder="Ej: 1024"
                  className="w-full rounded-xl border border-[rgba(var(--border),0.6)] bg-[rgba(var(--subpanel),0.6)] px-3 py-2 text-sm text-[rgb(var(--fg))] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[rgb(var(--ring))]"
                />
              </Field>
            </div>
            <div className="col-span-12 md:col-span-3">
              <Field label="Sucursal" htmlFor="sucursal" hint="ID sucursal">
                <input
                  id="sucursal"
                  value={filterForm.sucursal}
                  onChange={event => setFilterForm(prev => ({ ...prev, sucursal: event.target.value }))}
                  placeholder="Ej: 2"
                  className="w-full rounded-xl border border-[rgba(var(--border),0.6)] bg-[rgba(var(--subpanel),0.6)] px-3 py-2 text-sm text-[rgb(var(--fg))] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[rgb(var(--ring))]"
                />
              </Field>
            </div>
            <div className="col-span-12 sm:col-span-6 md:col-span-3">
              <Field label="Estado" htmlFor="estado">
                <select
                  id="estado"
                  value={filterForm.estado}
                  onChange={event => setFilterForm(prev => ({ ...prev, estado: event.target.value }))}
                  className="w-full rounded-xl border border-[rgba(var(--border),0.6)] bg-[rgba(var(--subpanel),0.6)] px-3 py-2 text-sm text-[rgb(var(--fg))] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[rgb(var(--ring))]"
                >
                  {ESTADOS.map(option => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </Field>
            </div>
            <div className="col-span-12 sm:col-span-6 md:col-span-3">
              <Field label="Orden" htmlFor="order">
                <select
                  id="order"
                  value={`${order.field}:${order.direction}`}
                  onChange={handleOrderChange}
                  className="w-full rounded-xl border border-[rgba(var(--border),0.6)] bg-[rgba(var(--subpanel),0.6)] px-3 py-2 text-sm text-[rgb(var(--fg))] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[rgb(var(--ring))]"
                >
                  {ORDENES.map(option => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </Field>
            </div>
            <div className="col-span-12 sm:col-span-6 md:col-span-3">
              <Field label="Desde" htmlFor="fechaDesde">
                <input
                  id="fechaDesde"
                  type="date"
                  value={filterForm.fechaDesde}
                  onChange={event => setFilterForm(prev => ({ ...prev, fechaDesde: event.target.value }))}
                  className="w-full rounded-xl border border-[rgba(var(--border),0.6)] bg-[rgba(var(--subpanel),0.6)] px-3 py-2 text-sm text-[rgb(var(--fg))] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[rgb(var(--ring))]"
                />
              </Field>
            </div>
            <div className="col-span-12 sm:col-span-6 md:col-span-3">
              <Field label="Hasta" htmlFor="fechaHasta">
                <input
                  id="fechaHasta"
                  type="date"
                  value={filterForm.fechaHasta}
                  onChange={event => setFilterForm(prev => ({ ...prev, fechaHasta: event.target.value }))}
                  className="w-full rounded-xl border border-[rgba(var(--border),0.6)] bg-[rgba(var(--subpanel),0.6)] px-3 py-2 text-sm text-[rgb(var(--fg))] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[rgb(var(--ring))]"
                />
              </Field>
            </div>
            <div className="col-span-12 flex items-end justify-end">
              <button
                type="submit"
                className="rounded-xl bg-[rgb(var(--brand))] px-4 py-2 text-sm font-semibold text-white shadow focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[rgb(var(--ring))] hover:bg-[rgb(var(--brand-700))]"
              >
                Aplicar filtros
              </button>
            </div>
          </form>

          <div className="mt-8 overflow-x-auto">
            <table className="min-w-full table-auto border-separate border-spacing-y-2">
              <thead>
                <tr className="text-left text-xs uppercase tracking-wide text-[rgba(var(--muted),0.9)]">
                  <th className="px-3 py-2">Fecha</th>
                  <th className="px-3 py-2">Empleado</th>
                  <th className="px-3 py-2">Sucursal</th>
                  <th className="px-3 py-2">Check-in</th>
                  <th className="px-3 py-2">Check-out</th>
                  <th className="px-3 py-2">Método</th>
                  <th className="px-3 py-2">Estado</th>
                  <th className="px-3 py-2 text-right">Acciones</th>
                </tr>
              </thead>
              <tbody>
                {fetching ? (
                  <tr>
                    <td colSpan={8} className="px-3 py-6 text-center text-sm text-[rgba(var(--muted),0.9)]">
                      Cargando asistencias...
                    </td>
                  </tr>
                ) : null}

                {error ? (
                  <tr>
                    <td colSpan={8} className="px-3 py-6 text-center text-sm text-[rgba(var(--muted),0.9)]">
                      No pudimos cargar las asistencias. Intentalo nuevamente.
                    </td>
                  </tr>
                ) : null}

                {isEmpty ? (
                  <tr>
                    <td colSpan={8} className="px-3 py-6">
                      <EmptyState
                        title="Sin asistencias"
                        description="No hay fichadas que coincidan con los filtros seleccionados. Ajustá los criterios o generá una nueva asistencia."
                      />
                    </td>
                  </tr>
                ) : null}

                {asistencias.map(asistencia => (
                  <tr key={asistencia.id} className="rounded-2xl bg-[rgba(var(--subpanel),0.35)] text-sm text-[rgb(var(--fg))] shadow-[0_4px_16px_rgba(var(--shadow),0.06)]">
                    <td className="rounded-l-2xl px-3 py-3 align-top">{formatDate(asistencia.fecha)}</td>
                    <td className="px-3 py-3 align-top">
                      <div className="flex flex-col">
                        <span className="font-medium">{asistencia.empleadoNombre}</span>
                        <span className="text-xs text-[rgba(var(--muted),0.9)]">ID {asistencia.empleadoId}</span>
                      </div>
                    </td>
                    <td className="px-3 py-3 align-top text-sm">{asistencia.sucursalNombre || asistencia.sucursalId}</td>
                    <td className="px-3 py-3 align-top">{formatTime(asistencia.checkIn)}</td>
                    <td className="px-3 py-3 align-top">{formatTime(asistencia.checkOut)}</td>
                    <td className="px-3 py-3 align-top">{asistencia.metodo}</td>
                    <td className="px-3 py-3 align-top">
                      <span className={`inline-flex items-center rounded-full px-3 py-1 text-xs font-semibold ${getEstadoClass(asistencia.estado)}`}>
                        {asistencia.estado}
                      </span>
                    </td>
                    <td className="rounded-r-2xl px-3 py-3 align-top">
                      <div className="flex justify-end gap-2">
                        <button
                          type="button"
                          onClick={() => navigate(`/rrhh/asistencias/${asistencia.id}/editar`)}
                          className="rounded-lg border border-[rgba(var(--border),0.4)] px-3 py-1.5 text-xs font-semibold text-[rgb(var(--fg))] transition hover:bg-[rgba(var(--panel),0.9)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[rgb(var(--ring))]"
                        >
                          Editar
                        </button>
                        <button
                          type="button"
                          onClick={() => handleDelete(asistencia.id)}
                          className="rounded-lg bg-[rgba(var(--fg),0.08)] px-3 py-1.5 text-xs font-semibold text-[rgb(var(--fg))] transition hover:bg-[rgba(var(--fg),0.12)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[rgb(var(--ring))]"
                        >
                          Eliminar
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="mt-6 flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
            <p className="text-sm text-[rgba(var(--muted),0.9)]">
              Mostrando {asistencias.length} de {data?.asistencias?.total ?? asistencias.length} asistencias
            </p>
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={() => setPagination(prev => ({ ...prev, page: Math.max(1, (prev.page || 1) - 1) }))}
                disabled={(pageInfo.page || 1) <= 1}
                className="rounded-xl border border-[rgba(var(--border),0.6)] bg-[rgba(var(--panel),0.7)] px-3 py-1.5 text-sm font-medium text-[rgba(var(--muted),0.95)] transition focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[rgb(var(--ring))] disabled:cursor-not-allowed disabled:opacity-60"
              >
                Anterior
              </button>
              <span className="text-sm font-medium text-[rgb(var(--fg))]">
                Página {pageInfo.page || 1} de {pageInfo.totalPages || 1}
              </span>
              <button
                type="button"
                onClick={() => setPagination(prev => ({ ...prev, page: (prev.page || 1) + 1 }))}
                disabled={(pageInfo.page || 1) >= (pageInfo.totalPages || 1)}
                className="rounded-xl border border-[rgba(var(--border),0.6)] bg-[rgba(var(--panel),0.7)] px-3 py-1.5 text-sm font-medium text-[rgba(var(--muted),0.95)] transition focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[rgb(var(--ring))] disabled:cursor-not-allowed disabled:opacity-60"
              >
                Siguiente
              </button>
            </div>
          </div>
        </div>
      </section>

    </div>
  );
}
